import { Box, Button, IconButton, useTheme } from '@mui/material';
import { HeaderContext } from '@tanstack/table-core';
import { useTimesheetRouteId } from '../../context/TimesheetRouteIdContext';
import { Typography } from '../../../../components/Typography/Typography';
import { useLoadingBar } from '../../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { useClearTimesheetColumn } from '../../../../queries/hooks/useClearTimesheetColumn';
import {
  LoadingId,
  TimesheetRow,
  TimesheetStatus,
  TimesheetToOverideData,
  WeekDay,
} from '../../../../types';
import { useSingleTimesheet } from '../../../../queries/hooks/useSingleTimesheet';
import { useCallback, useMemo, useState } from 'react';
import { ReactComponent as CalendarDisabledIcon } from '../../../../assets/icons/calendar-disabled.svg';
import TooltipComponent from '../../../../components/TooltipComponent/TooltipComponent';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadingClearColumn, overideDataAtom } from '../../../../state/UITimesheetsState';
import { isSmallScreenState } from '../../../../state/UIState';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';

interface Props {
  row: HeaderContext<TimesheetRow, number>;
  text: string;
  style?: React.CSSProperties;
  /** Month read-only / submitted: label only, no Clear or override. */
  hideActions?: boolean;
}

export const TableHeaderCellWithClear = ({ row, text, style, hideActions }: Props) => {
  const id = useTimesheetRouteId();
  const [showToolip, setShowToolip] = useState(false);
  const setLoading = useSetAtom(loadingClearColumn);
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const setOverideHolidayData = useSetAtom(overideDataAtom);

  const { data: singleTimesheet, isLoading: tsLoading } = useSingleTimesheet({
    id: Number(id),
  });

  const { colors } = useTheme();
  const { pushErrorToast, pushSuccessToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();

  const holiday = useMemo(() => {
    if (!text) return;
    const dayLabel = Number(text.split(',')[1].split('/')[1]);
    const monthLabel = Number(text.split(',')[1].split('/')[0]);

    return singleTimesheet?.holidays.find(
      (holiday) => holiday.day === dayLabel && holiday.month === monthLabel
    );
  }, [singleTimesheet, text]);

  const onOverideClick = useCallback(() => {
    const dayNameShort = text.split(',')[0];
    const dayNameMap: Record<string, WeekDay> = {
      Mon: 'monday',
      Tue: 'tuesday',
      Wed: 'wednesday',
      Thu: 'thursday',
      Fri: 'friday',
    };
    const weekday = dayNameMap[dayNameShort];

    const data: TimesheetToOverideData = {
      header: text,
      holidayName: holiday?.name || '',
      weekday,
      day: holiday?.day || 0,
      tsId: singleTimesheet?.id || 0,
      data:
        singleTimesheet?.timesheetRows.map((r) => ({
          id: r.id,
          name: r.name,
          created_at: r.created_at,
          updated_at: r.updated_at,
          dealId: r.dealId,
          data: r[row.header.id as WeekDay],
        })) || [],
    };
    setOverideHolidayData(data);
  }, [
    text,
    holiday?.name,
    holiday?.day,
    singleTimesheet?.id,
    singleTimesheet?.timesheetRows,
    setOverideHolidayData,
    row.header.id,
  ]);

  const weekDay = row.header.id as WeekDay;
  const shouldDisableDay = singleTimesheet?.timesheetRows[0][weekDay] === null;
  const { mutate: onClearColumn } = useClearTimesheetColumn();
  const disableClear =
    hideActions ||
    shouldDisableDay ||
    (singleTimesheet?.status !== TimesheetStatus.ISSUED &&
      singleTimesheet?.status !== TimesheetStatus.REJECTED) ||
    tsLoading;

  const canOveride =
    !hideActions &&
    (singleTimesheet?.status === TimesheetStatus.ISSUED ||
      singleTimesheet?.status === TimesheetStatus.REJECTED) &&
    !!holiday;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        gap: isSmallScreen ? '0px' : '5px',
        alignItems: isSmallScreen ? 'flex-start' : 'center',
        width: '100%',
        justifyContent: 'space-between',
        padding: isSmallScreen ? '3px 0' : 'unset',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '5px',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='subtitle1'
          color={shouldDisableDay ? colors.textAccent.disabled : colors.text.main}
          style={{ letterSpacing: '0.25px', ...style }}
        >
          {text}
        </Typography>
        {!!holiday && (
          <Box marginTop='4px'>
            <TooltipComponent
              open={showToolip}
              onClose={() => setShowToolip(false)}
              onOpen={() => setShowToolip(true)}
              placement='top'
              content={
                <Typography variant='body' color={colors.text.main}>
                  {holiday.name}
                </Typography>
              }
            >
              <CalendarDisabledIcon
                onMouseEnter={() => setShowToolip(true)}
                onMouseLeave={() => setShowToolip(false)}
              />
            </TooltipComponent>
          </Box>
        )}
      </Box>
      {!disableClear && (
        <Button
          variant='text'
          sx={{
            justifyContent: isSmallScreen ? 'flex-start' : 'center',
            padding: isSmallScreen ? '0px' : 'inherit',
            paddingRight: 0,
          }}
          onClick={() => {
            startLoading(LoadingId.clearColumn);
            setLoading(true);
            onClearColumn(
              {
                timesheetId: Number(id),
                weekDay,
              },
              {
                onSuccess: () => {
                  pushSuccessToast({
                    title: 'Column is Cleared',
                    message: `The column has been cleared successfully.`,
                  });
                  setLoading(false);
                },
                onError: (error: unknown) => {
                  pushErrorToast({ message: extractErrorMessage(error) });
                },
                onSettled: () => {
                  stopLoading(LoadingId.clearColumn);
                },
              }
            );
          }}
        >
          <Typography color={colors.textAccent.default} variant='subtitle1'>
            Clear
          </Typography>
        </Button>
      )}
      {!!canOveride && (
        <IconButton
          onClick={onOverideClick}
          sx={{
            svg: { path: { fill: colors.icon.accent, opacity: '1' } },
            width: '34px',
            height: '34px',
            padding: '0px',
            borderRadius: '50%',
            ':hover': {
              backgroundColor: colors.surfaceBackground.highlighted,
            },
          }}
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );
};
